import React, { useEffect, useState } from 'react';
import { Badge, Card, Col, Container, Nav, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_URL;

const JBranchAuditChecklist = () => {
  const [assignedChecklists, setAssignedChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState({});
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'all');
  const navigate = useNavigate();

  const auditorData = JSON.parse(localStorage.getItem("auditor"));
  const auditorId = auditorData?.id;
  const BranchCode = auditorData?.BranchCode;

  const notify = (message, type = 'success') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  useEffect(() => {
    const fetchAssignedChecklists = async () => {
      if (!auditorId || !BranchCode) {
        notify("Auditor ID or Branch Code is missing", "error");
        setLoading(false);
        return;
      }

      try {
        const url = `${BASE_URL}/get-assigned-checklists/${auditorId}/${BranchCode}`;

        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch checklists: ${response.status}`);
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          console.error('Received non-array data:', data);
          setAssignedChecklists([]);
        } else {
          setAssignedChecklists(data);
        }

      } catch (error) {
        console.error("Error details:", error);
        notify("Failed to load assigned checklists", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchAssignedChecklists();
  }, [auditorId, BranchCode]);

  useEffect(() => {
    const fetchAllProgress = async () => {
      for (const checklist of assignedChecklists) {
        await fetchProgress(checklist);
      }
    };
    
    if (assignedChecklists.length > 0) {
      fetchAllProgress();
    }
  }, [assignedChecklists]);

  const fetchProgress = async (checklist) => {
    try {
      // Updated to include branch_id parameter
      const response = await fetch(
        `${BASE_URL}/get-checklist-progress/${checklist.checklist_id}/${checklist.BranchCode}/${auditorId}/${checklist.branch_id}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch progress');
      
      const data = await response.json();
      
      // Use a unique key that combines checklist_id and branch_id for more specific tracking
      const progressKey = `${checklist.checklist_id}_${checklist.branch_id}`;
      
      setProgressData(prevState => ({
        ...prevState,
        [progressKey]: data
      }));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in progress':
        return 'warning';
      case 'pending':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const handleStartAudit = (checklist) => {
    navigate('/JBranchAuditDetails', {
      state: { 
        checklist: {
          checklist_id: checklist.checklist_id,
          BranchCode: checklist.BranchCode,
          checklist_name: checklist.checklist_name,
          branches: checklist.branches,
          status: checklist.status,
          id: checklist.id,
          branch_id: checklist.branch_id,
        }
      }
    });
  };

  const CircularProgress = ({ percentage, size = 60 }) => {
    const strokeWidth = 8;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;

    const getProgressColor = (percent) => {
      if (percent >= 100) return '#198754';
      if (percent >= 75) return '#0d6efd';
      if (percent >= 50) return '#ffc107';
      if (percent >= 25) return '#fd7e14';
      return '#dc3545';
    };

    const progressColor = getProgressColor(percentage);

    return (
      <div className="circular-progress" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle
            className="progress-bg"
            stroke="#e9ecef"
            strokeWidth={strokeWidth}
            fill="none"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <circle
            className="progress-bar"
            stroke={progressColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            fill="none"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dy=".1em"
            dominantBaseline="middle"
            className="progress-text"
            style={{ fill: progressColor }}
          >
            {percentage}%
          </text>
        </svg>
      </div>
    );
  };

  const filterChecklistsByStatus = (status) => {
    if (status === 'all') return assignedChecklists;
    return assignedChecklists.filter(checklist => 
      checklist.status.toLowerCase() === status.toLowerCase()
    );
  };

  const getTabCount = (status) => {
    if (status === 'all') return assignedChecklists.length;
    return assignedChecklists.filter(checklist => 
      checklist.status.toLowerCase() === status.toLowerCase()
    ).length;
  };

  // Helper function to get progress data for a specific checklist
  const getProgressForChecklist = (checklist) => {
    const progressKey = `${checklist.checklist_id}_${checklist.branch_id}`;
    return progressData[progressKey];
  };

  if (loading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center py-5">
          <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-primary fw-bold mb-1">Loading Assigned Checklists</p>
          <p className="text-muted">Please wait while we fetch your assigned audit checklists...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 py-md-4" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer />
      
      <div className="mb-3 mb-md-4">
        <h2 className="text-primary h4 h2-md mb-2">My Assigned Checklists</h2>
        <p className="text-muted small mb-0">Manage and execute your assigned audit checklists</p>
      </div>

      <div className="mb-4">
        <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav.Item>
            <Nav.Link eventKey="all" className="d-flex align-items-center gap-2">
              All
              <Badge bg="secondary" pill>{getTabCount('all')}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="pending" className="d-flex align-items-center gap-2">
              Pending
              <Badge bg="danger" pill>{getTabCount('pending')}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="in progress" className="d-flex align-items-center gap-2">
              In Progress
              <Badge bg="warning" pill>{getTabCount('in progress')}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="completed" className="d-flex align-items-center gap-2">
              Completed
              <Badge bg="success" pill>{getTabCount('completed')}</Badge>
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </div>
      
      <Row xs={1} md={2} lg={3} className="g-3 g-md-4 justify-content-start card-grid">
        {filterChecklistsByStatus(activeTab).map((checklist) => {
          const checklistProgress = getProgressForChecklist(checklist);
          
          return (
            <Col key={`${checklist.checklist_id}_${checklist.branch_id}`} className="card-column">
              <Card className="h-100 shadow-sm hover-shadow card-audit" 
                    style={{ transition: 'all 0.3s ease-in-out' }}>
                <Card.Header className="bg-white border-bottom-0 p-3">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="flex-grow-1">
                      <h5 className="text-primary h6 mb-1 text-truncate">
                        {checklist.branches}
                      </h5>
                      <div className="text-muted small text-truncate">
                        {checklist.checklist_name || `Checklist #${checklist.checklist_id}`}
                      </div>
                    </div>
                    <Badge 
                      bg={getStatusBadgeColor(checklist.status)}
                      className="px-2 py-1 rounded-pill status-badge"
                    >
                      {checklist.status}
                    </Badge>
                  </div>
                </Card.Header>
                
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
                    {checklistProgress ? (
                      <CircularProgress 
                        percentage={checklistProgress.progressPercentage || 0}
                        size={50}
                      />
                    ) : (
                      <div className="progress-loading text-center" style={{ width: 50, height: 50 }}>
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                    <div className="progress-stats flex-grow-1">
                      <div className="stat-item d-flex justify-content-between align-items-center">
                        <small className="text-muted">Completed:</small>
                        <span className="fw-medium">
                          {checklistProgress?.answeredQuestions || 0}
                        </span>
                      </div>
                      <div className="stat-item d-flex justify-content-between align-items-center">
                        <small className="text-muted">Total:</small>
                        <span>
                          {checklistProgress?.totalQuestions || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button 
                    className="btn btn-primary w-100 rounded-3"
                    onClick={() => handleStartAudit(checklist)}
                  >
                    <i className="fas fa-clipboard-check me-2"></i>
                    {checklist.status.toLowerCase() === 'completed' ? 'View Details' : 'Start Audit'}
                  </button>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {filterChecklistsByStatus(activeTab).length === 0 && (
        <div className="text-center py-5">
          <div className="mb-3">
            <i className="fas fa-clipboard-list fa-3x text-muted"></i>
          </div>
          <h4 className="h5 text-muted mb-2">
            {activeTab === 'all' ? 'No checklists assigned yet' : `No ${activeTab} checklists`}
          </h4>
          <p className="text-muted small mb-0">
            {activeTab === 'all' 
              ? 'When checklists are assigned to you, they will appear here.'
              : `No checklists with ${activeTab} status found.`
            }
          </p>
        </div>
      )}

      {/* Keep all the existing styles */}
      <style>
        {`
          .hover-shadow {
            transition: all 0.3s ease-in-out;
          }
          
          .hover-shadow:hover {
            transform: translateY(-2px);
            box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
          }
          
          .circular-progress {
            position: relative;
          }
          
          .progress-bg {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
          }
          
          .progress-bar {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
            transition: stroke-dashoffset 0.35s, stroke 0.35s;
          }
          
          .progress-text {
            font-size: 12px;
            font-weight: 600;
            transition: fill 0.35s;
          }
          
          .progress-stats {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .stat-item {
            font-size: 0.875rem;
          }

          @media (min-width: 768px) {
            .h2-md {
              font-size: 2rem;
            }
            
            .progress-text {
              font-size: 14px;
            }
          }

          @media (max-width: 576px) {
            .card {
              margin: 0 0.5rem;
            }
            
            .container-fluid {
              padding-left: 1rem;
              padding-right: 1rem;
            }
          }

          .status-badge {
            font-size: 0.7rem;
            white-space: nowrap;
            flex-shrink: 0;
            max-width: 90px;
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
            display: inline-block;
            vertical-align: top;
            margin-top: 2px;
            line-height: 1.2;
          }

          .card-header .d-flex {
            min-height: 50px;
            gap: 8px !important;
          }

          .card-grid {
            display: flex;
            flex-wrap: wrap;
          }

          .card-column {
            display: flex;
            padding: 0.75rem;
          }

          .card-audit {
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
            border-radius: 8px;
            overflow: hidden;
          }

          @media (max-width: 768px) {
            .card-column {
              padding: 0.5rem;
            }
            
            .card {
              margin: 0 0.5rem;
            }
            
            .container-fluid {
              padding-left: 1rem;
              padding-right: 1rem;
            }
          }

          @media (min-width: 992px) {
            .card-grid {
              justify-content: flex-start;
            }
            
            .card-grid:not(.row-fill) {
              justify-content: center;
            }
          }

          .spinner-grow {
            animation-duration: 1.5s;
          }
          
          .text-primary.fw-bold {
            font-size: 1.2rem;
          }
          
          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
          
          .loading-fadeout {
            animation: fadeOut 0.5s ease-out forwards;
          }
          
          .progress-loading {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #f8f9fa;
            border-radius: 50%;
          }
          
          .progress-loading .spinner-border {
            width: 1.5rem;
            height: 1.5rem;
          }
          
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.6; }
            100% { opacity: 1; }
          }
          
          .card-loading {
            animation: pulse 1.5s infinite ease-in-out;
          }

          .nav-tabs {
            border-bottom: 2px solid #dee2e6;
          }
          
          .nav-tabs .nav-link {
            color: #6c757d;
            border: none;
            border-bottom: 2px solid transparent;
            padding: 0.75rem 1rem;
            font-weight: 500;
            transition: all 0.3s ease;
          }
          
          .nav-tabs .nav-link:hover {
            border-bottom-color: #0d6efd;
            color: #0d6efd;
          }
          
          .nav-tabs .nav-link.active {
            color: #0d6efd;
            background-color: transparent;
            border-bottom-color: #0d6efd;
          }
          
          .nav-tabs .nav-link .badge {
            font-size: 0.7rem;
            margin-left: 0.25rem;
          }
        `}
      </style>
    </Container>
  );
};

export default JBranchAuditChecklist;